import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Clock, DollarSign, ArrowRight, FileText, Paperclip, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, resolveFileUrl } from "../../api";

export default function JobDetailModal({ jid, onClose, isOwner, user }) {
  const [job, setJob] = useState(null);
  const [apps, setApps] = useState([]);
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [appForm, setAppForm] = useState({ cover_letter: "", rate: "", attachment_url: "", attachment_name: "", portfolio_url: "" });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${jid}`).then((r) => setJob(r.data));
    if (user) {
      api.get("/my/applications").then((r) => setHasApplied(r.data.some((a) => a.job_id === jid))).catch(() => {});
    }
  }, [jid, user]);

  useEffect(() => {
    if (isOwner) api.get(`/jobs/${jid}/applications`).then((r) => setApps(r.data)).catch(() => {});
  }, [jid, isOwner]);

  const uploadAttachment = async (file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().split(".").pop();
    const kind = ext === "zip" ? "zip" : "pdf";
    if (!["pdf", "zip"].includes(kind)) {
      toast.error("Attachments must be a PDF resume or a ZIP archive.");
      return;
    }
    const cap = kind === "pdf" ? 10 : 25;
    if (file.size > cap * 1024 * 1024) {
      toast.error(`File too large. ${kind.toUpperCase()} max is ${cap} MB.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const { data } = await api.post("/uploads/attachment", fd);
      setAppForm((s) => ({ ...s, attachment_url: data.url, attachment_name: file.name }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submitApplication = async () => {
    setApplying(true);
    try {
      await api.post(`/jobs/${jid}/apply`, {
        cover_letter: appForm.cover_letter,
        rate: parseFloat(appForm.rate) || 0,
        attachment_url: appForm.attachment_url || "",
        portfolio_url: appForm.portfolio_url || "",
      });
      setHasApplied(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply");
    } finally { setApplying(false); }
  };

  const updateAppStatus = async (aid, status) => {
    await api.put(`/applications/${aid}/status?status=${status}`);
    api.get(`/jobs/${jid}/applications`).then((r) => setApps(r.data));
    api.get(`/jobs/${jid}`).then((r) => setJob(r.data));
  };

  if (!job) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center" onClick={() => onClose(false)}>
        <div className="font-mono-label text-[10px] text-neutral-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 flex items-start justify-center p-4 overflow-y-auto" onClick={() => onClose(false)} data-testid="job-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] border border-neutral-800 p-6 max-w-3xl w-full my-8">
        <div className="flex justify-between items-start mb-6">
          <div className="flex-1 min-w-0">
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">{job.category?.toUpperCase()} · {job.job_type?.replace("_", " ").toUpperCase()}</div>
            <h3 className="font-display text-3xl sm:text-4xl uppercase leading-tight">{job.title}</h3>
            <div className="font-mono-label text-[11px] text-neutral-400 mt-2">
              {job.employer_company || job.employer_name} · {(job.location || "Remote").toUpperCase()}
            </div>
          </div>
          <button onClick={() => onClose(false)}><X size={18} /></button>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          {job.remote && <span className="chip">REMOTE</span>}
          {(job.budget_min > 0 || job.budget_max > 0) && (
            <span className="chip chip-accent">
              <DollarSign size={10} className="inline" />
              {(job.budget_min || 0).toLocaleString()}{job.budget_max && job.budget_max !== job.budget_min ? `-$${job.budget_max.toLocaleString()}` : ""}/{job.budget_unit}
            </span>
          )}
          <span className="chip"><Clock size={10} className="inline mr-1" />Posted {new Date(job.created_at).toLocaleDateString()}</span>
          <span className="chip">{job.applicants_count || 0} APPLIED</span>
        </div>

        <p className="text-neutral-300 whitespace-pre-line mb-5">{job.description}</p>

        {(job.skills || []).length > 0 && (
          <div className="mb-6">
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">SKILLS</div>
            <div className="flex flex-wrap gap-2">
              {job.skills.map((s) => <span key={s} className="font-mono-label text-[10px] text-neutral-300 border border-neutral-800 px-2 py-1">{s.toUpperCase()}</span>)}
            </div>
          </div>
        )}

        {/* Apply form */}
        {!isOwner && job.status === "open" && user && !hasApplied && (
          <ApplyForm
            appForm={appForm}
            setAppForm={setAppForm}
            uploading={uploading}
            uploadAttachment={uploadAttachment}
            applying={applying}
            submitApplication={submitApplication}
          />
        )}
        {hasApplied && !isOwner && (
          <div className="border border-[var(--accent)] bg-[#1a0a05] p-5 mb-6 font-mono-label text-[11px] text-[var(--accent)]" data-testid="apply-already">
            ✓ APPLICATION SUBMITTED — THE EMPLOYER WILL CONTACT YOU IF SHORTLISTED.
          </div>
        )}
        {!user && (
          <Link to="/login" className="btn-primary" data-testid="apply-login">Sign in to apply <ArrowRight size={14} /></Link>
        )}

        {/* Applicants list (employer) */}
        {isOwner && <ApplicantsList apps={apps} updateAppStatus={updateAppStatus} />}
      </div>
    </div>
  );
}

function ApplyForm({ appForm, setAppForm, uploading, uploadAttachment, applying, submitApplication }) {
  return (
    <div className="border border-neutral-800 p-5 mb-6">
      <div className="font-mono-label text-[10px] text-[var(--accent)] mb-3">YOUR APPLICATION</div>
      <textarea rows={4} className="input-brut resize-none mb-3" placeholder="Pitch yourself — relevant work, why you're the right person…" value={appForm.cover_letter} onChange={(e) => setAppForm((s) => ({ ...s, cover_letter: e.target.value }))} data-testid="apply-cover-letter" />
      <input type="number" className="input-brut mb-3" placeholder="Your rate / quote (USD, optional)" value={appForm.rate} onChange={(e) => setAppForm((s) => ({ ...s, rate: e.target.value }))} data-testid="apply-rate" />
      <input type="url" className="input-brut mb-3" placeholder="Portfolio link (optional — Behance, IG, your site…)" value={appForm.portfolio_url} onChange={(e) => setAppForm((s) => ({ ...s, portfolio_url: e.target.value }))} data-testid="apply-portfolio-url" />

      <div className="mb-3">
        <div className="font-mono-label text-[10px] text-neutral-400 mb-2">ATTACH RESUME OR MEDIA KIT (PDF · 10MB · or ZIP · 25MB)</div>
        {appForm.attachment_url ? (
          <div className="flex items-center justify-between gap-3 border border-[var(--accent)] bg-[#1a0a05] px-3 py-2" data-testid="apply-attachment-preview">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle2 size={14} className="text-[var(--accent)] shrink-0" />
              <a href={resolveFileUrl(appForm.attachment_url)} target="_blank" rel="noreferrer" className="font-mono-label text-[11px] text-white truncate hover:text-[var(--accent)]" data-testid="apply-attachment-link">
                {appForm.attachment_name || "Attached file"}
              </a>
            </div>
            <button
              type="button"
              onClick={() => setAppForm((s) => ({ ...s, attachment_url: "", attachment_name: "" }))}
              className="font-mono-label text-[10px] text-neutral-400 hover:text-[var(--accent)]"
              data-testid="apply-attachment-remove"
            >
              REMOVE
            </button>
          </div>
        ) : (
          <label className={`flex items-center gap-2 border border-dashed border-neutral-700 px-3 py-2.5 cursor-pointer hover:border-[var(--accent)] transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="apply-attachment-label">
            <Paperclip size={14} className="text-neutral-400" />
            <span className="font-mono-label text-[11px] text-neutral-300">
              {uploading ? "Uploading…" : "ATTACH FILE"}
            </span>
            <input
              type="file"
              accept=".pdf,.zip,application/pdf,application/zip"
              className="hidden"
              onChange={(e) => uploadAttachment(e.target.files?.[0])}
              disabled={uploading}
              data-testid="apply-attachment-input"
            />
          </label>
        )}
      </div>

      <button onClick={submitApplication} disabled={applying || !appForm.cover_letter.trim() || uploading} className="btn-primary" data-testid="apply-submit">
        {applying ? "Submitting…" : "Apply Now"}
      </button>
    </div>
  );
}

function ApplicantsList({ apps, updateAppStatus }) {
  return (
    <div>
      <div className="font-mono-label text-[10px] text-[var(--accent)] mb-3">APPLICANTS · {apps.length}</div>
      {apps.length === 0 ? (
        <p className="font-mono-label text-[10px] text-neutral-500">No applicants yet.</p>
      ) : (
        <div className="space-y-3" data-testid="employer-apps-list">
          {apps.map((a) => (
            <ApplicantRow key={a.id} a={a} updateAppStatus={updateAppStatus} />
          ))}
        </div>
      )}
    </div>
  );
}

function ApplicantRow({ a, updateAppStatus }) {
  return (
    <div className="border border-neutral-800 p-4" data-testid={`app-row-${a.id}`}>
      <div className="flex items-start gap-3">
        <Link to={`/profile/${a.applicant_username}`} className="shrink-0">
          {a.applicant_avatar ? (
            <img src={resolveFileUrl(a.applicant_avatar)} alt="" className="w-10 h-10 object-cover border border-neutral-800" />
          ) : (
            <div className="w-10 h-10 border border-neutral-800 bg-neutral-900 flex items-center justify-center"><FileText size={14} /></div>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/profile/${a.applicant_username}`} className="font-headline text-sm uppercase hover:text-[var(--accent)]">{a.applicant_name}</Link>
            <span className={`chip ${a.status === "hired" ? "chip-accent" : ""}`}>{a.status?.toUpperCase()}</span>
            {a.rate > 0 && <span className="font-mono-label text-[10px] text-[var(--accent)]">${a.rate}</span>}
          </div>
          <div className="font-mono-label text-[10px] text-neutral-500 mt-1">{a.applicant_service_title}{a.applicant_city && ` · ${a.applicant_city}${a.applicant_state ? `, ${a.applicant_state}` : ""}`}</div>
          {a.cover_letter && <p className="text-neutral-300 text-sm mt-2 whitespace-pre-line">{a.cover_letter}</p>}
          {(a.attachment_url || a.portfolio_url) && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid={`app-attachments-${a.id}`}>
              {a.attachment_url && (
                <a
                  href={resolveFileUrl(a.attachment_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono-label text-[10px] inline-flex items-center gap-1 px-2 py-1 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition-colors"
                  data-testid={`app-attachment-${a.id}`}
                >
                  <Paperclip size={11} /> RESUME / FILE
                </a>
              )}
              {a.portfolio_url && (
                <a
                  href={a.portfolio_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono-label text-[10px] inline-flex items-center gap-1 px-2 py-1 border border-neutral-800 text-neutral-300 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
                  data-testid={`app-portfolio-${a.id}`}
                >
                  <ArrowRight size={11} /> PORTFOLIO
                </a>
              )}
            </div>
          )}
          <div className="flex gap-2 mt-3 flex-wrap">
            <button onClick={() => updateAppStatus(a.id, "shortlisted")} className="font-mono-label text-[10px] px-2 py-1 border border-neutral-800 hover:border-[var(--accent)]" data-testid={`shortlist-${a.id}`}>SHORTLIST</button>
            <button onClick={() => updateAppStatus(a.id, "hired")} className="font-mono-label text-[10px] px-2 py-1 border border-[var(--accent)] text-[var(--accent)]" data-testid={`hire-${a.id}`}>HIRE</button>
            <button onClick={() => updateAppStatus(a.id, "rejected")} className="font-mono-label text-[10px] px-2 py-1 border border-neutral-800 hover:text-[var(--accent)]" data-testid={`reject-${a.id}`}>REJECT</button>
          </div>
        </div>
      </div>
    </div>
  );
}
